import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, retry, timeout } from 'rxjs';
import { environment } from '../../../environments/environment';
import { SKIP_ERROR_PAGE, SKIP_LOADER } from '../constants/http-context.constants';

@Injectable({
  providedIn: 'root',
})
/**
 * Provides HTTP methods that prefix endpoints with the configured API base URL.
 */
export class HttpUtilityService {
  private readonly http = inject(HttpClient);
  private readonly appAPIBaseURL = environment.apiEndpoint;
  private readonly requestTimeout = environment.requestTimeout;
  private readonly retryCount = environment.httpRetryCount;

  /** When showLoader is false, tag the request context so the loadingInterceptor skips the spinner. */
  private loaderContext(showLoader: boolean): HttpContext {
    return new HttpContext().set(SKIP_LOADER, !showLoader);
  }

  /**
   * Retrieves data from the specified API endpoint.
   * @param endpoint Relative API endpoint to request.
   * @param params Optional query parameters to include in the request.
   * @param showLoader Set false to suppress the global loader (e.g. background calls).
   * @param skipErrorPage Set true so a 401/403 does not redirect to the unauthorized page.
   * @returns An observable that times out after the configured duration and retries safe reads.
   */
  get<T>(
    endpoint: string,
    params?: HttpParams,
    showLoader = true,
    skipErrorPage = false,
  ): Observable<T> {
    return this.http
      .get<T>(`${this.appAPIBaseURL}${endpoint}`, {
        params,
        context: this.loaderContext(showLoader).set(SKIP_ERROR_PAGE, skipErrorPage),
      })
      .pipe(timeout(this.requestTimeout), retry({ count: this.retryCount, delay: 1000 }));
  }

  /**
   * Sends data to the specified API endpoint.
   * @param endpoint Relative API endpoint to request.
   * @param body Payload to send in the request body.
   * @param params Optional query parameters to include in the request.
   * @param showLoader Set false to suppress the global loader (e.g. background calls).
   * @param requestTimeoutMs Overrides the default client timeout for slow writes (e.g. route save/publish).
   * @returns An observable that times out after the configured duration.
   */
  post<T, TBody = object>(
    endpoint: string,
    body: TBody,
    params?: HttpParams,
    showLoader = true,
    requestTimeoutMs = this.requestTimeout,
  ): Observable<T> {
    // No retry on writes — retrying could duplicate a server-side mutation.
    return this.http
      .post<T>(`${this.appAPIBaseURL}${endpoint}`, body, {
        params,
        context: this.loaderContext(showLoader),
      })
      .pipe(timeout(requestTimeoutMs));
  }

  /** Sends a POST request whose response body is a downloadable binary file. */
  postBlob<TBody = object>(endpoint: string, body: TBody, showLoader = true): Observable<Blob> {
    return this.http
      .post(`${this.appAPIBaseURL}${endpoint}`, body, {
        responseType: 'blob',
        context: this.loaderContext(showLoader),
      })
      .pipe(timeout(this.requestTimeout));
  }

  /** Sends a GET request whose response body is a downloadable binary file. */
  getBlob(endpoint: string, showLoader = true): Observable<Blob> {
    return this.http
      .get(`${this.appAPIBaseURL}${endpoint}`, {
        responseType: 'blob',
        context: this.loaderContext(showLoader),
      })
      .pipe(timeout(this.requestTimeout));
  }

  /**
   * Replaces data at the specified API endpoint.
   * @param endpoint Relative API endpoint to request.
   * @param body Payload to send in the request body.
   * @param showLoader Set false to suppress the global loader (e.g. background calls).
   * @returns An observable of the API response.
   */
  put<T, TBody = object>(endpoint: string, body: TBody, showLoader = true): Observable<T> {
    return this.http.put<T>(`${this.appAPIBaseURL}${endpoint}`, body, {
      context: this.loaderContext(showLoader),
    });
  }

  /**
   * Partially updates data at the specified API endpoint.
   * @param endpoint Relative API endpoint to request.
   * @param body Partial payload to send in the request body.
   * @param showLoader Set false to suppress the global loader (e.g. background calls).
   * @returns An observable of the API response.
   */
  patch<T, TBody = object>(endpoint: string, body: TBody, showLoader = true): Observable<T> {
    return this.http.patch<T>(`${this.appAPIBaseURL}${endpoint}`, body, {
      context: this.loaderContext(showLoader),
    });
  }

  /**
   * Deletes data at the specified API endpoint.
   * @param endpoint Relative API endpoint to request.
   * @param params Optional query parameters to include in the request.
   * @param showLoader Set false to suppress the global loader (e.g. background calls).
   * @returns An observable of the API response.
   */
  delete<T>(endpoint: string, params?: HttpParams, showLoader = true): Observable<T> {
    return this.http.delete<T>(`${this.appAPIBaseURL}${endpoint}`, {
      params,
      context: this.loaderContext(showLoader),
    });
  }
}
